import {
  eSettingsEnumKind,
  eTableName,
  eMitigationPolicyDocumentType,
  IClientSettings,
  IClientSettingsAssessmentLink,
  IClientSettingsCommonUX,
  IClientSettingsEmail,
  IClientSettingsEnums,
  IClientSettingsPositionGroup,
  IClientSettingsRiskHR,
  ICompanyInfo,
  ILCHttpResponse,
  IMitigationPolicyAssignResponse,
  IMitigationPolicyDeleteAllResponse,
  IMitigationPolicyFileSummary,
  IMitigationPolicyStatus,
  IMitigationPolicyUploadResponse,
  ISearch,
  ISeverityPositionItem,
  ITopicAssignment,
  ITopicAssignmentsResponse,
  ITmpURL,
  IUnassignedTopicSettings,
} from '@logicalcommander/types'
import { createApi } from '@reduxjs/toolkit/query/react'
import { v4 as uuidv4 } from 'uuid'
import { configManager } from '../../config/configManager'
import { RootState } from '../redux/store'
import { createBaseQueryWithErrorHandling } from './baseQueryWithErrorHandling'

interface ISeverityPosition {
  id: number
  topicsTRF: Record<string, number>
}
interface IImpact {
  topicId: number
  impactIds: string[]
}
interface IImpactPriority {
  [key: string]: number
}
interface IData {
  severityPositions: ISeverityPosition[]
  severityPositionsGroups: ISeverityPosition[]
  impacts: IImpact[]
  impactPriority: IImpactPriority
}
interface IImpactsResponseSettings {
  data: IData
  message: string
  statusCode: number
  timestamp: string
}
interface IImpactsRequestSettings {
  impactName: string
  topicIds: number[]
}
export interface IDeleteSavedFilter {
  name: string
  tableName: eTableName
}
export interface IAddSavedFilter extends IDeleteSavedFilter {
  filters: ISearch[]
}

export interface IUploadCompanyLogoResponse {
  url: ITmpURL
}

export interface ISsoAttributeMapping {
  email: string
  firstName?: string
  lastName?: string
  groups?: string
  ssoSubjectId?: string
}

export interface ISsoGroupRoleMapping {
  idpGroup: string
  lcRole: string
  /** Client-only stable row key for React lists; omitted from {@link buildSsoSettingsUpdatePayload}. */
  clientRowId?: string
}

export interface ISsoSettings {
  enabled: boolean
  idpMetadataUrl?: string
  idpEntityId?: string
  idpSsoUrl?: string
  idpCertificate?: string
  spEntityId?: string
  attributeMapping: ISsoAttributeMapping
  groupRoleMappings: ISsoGroupRoleMapping[]
  jitProvisioning: boolean
  ssoEnforced: boolean
}

/** Fields accepted by PUT `/settings/sso` — explicit shape, no `delete` / `as any`. */
export function buildSsoSettingsUpdatePayload(values: ISsoSettings): ISsoSettings {
  const groupRoleMappings = values.groupRoleMappings
    .map(({ idpGroup, lcRole }) => ({
      idpGroup: (idpGroup ?? '').trim(),
      lcRole: (lcRole ?? '').trim(),
    }))
    // Empty placeholder rows from "Add group mapping" must not be sent: gateway DTO uses @IsNotEmpty() on both fields.
    .filter((row) => row.idpGroup.length > 0 && row.lcRole.length > 0)

  return {
    enabled: values.enabled,
    idpMetadataUrl: values.idpMetadataUrl,
    idpEntityId: values.idpEntityId,
    idpSsoUrl: values.idpSsoUrl,
    idpCertificate: values.idpCertificate,
    spEntityId: values.spEntityId,
    attributeMapping: { ...values.attributeMapping },
    groupRoleMappings,
    jitProvisioning: values.jitProvisioning,
    ssoEnforced: values.ssoEnforced,
  }
}

export interface ISsoMetadataPreview {
  idpEntityId: string
  idpSsoUrl: string
  idpCertificate: string
  certExpiresAt?: string
}

export interface ISsoSpInfo {
  acsUrl: string
  spEntityId: string
  metadataUrl: string
}

export interface ISsoSettingsWithSpInfo {
  sso: ISsoSettings | null
  spInfo: ISsoSpInfo
}

export interface IUpdateSeveritySettings {
  severityPositionsGroups?: {
    id: number
    topicsTRF: { [key: string]: number }
  }[]
  severityPositions?: {
    id: number
    topicsTRF: { [key: string]: number }
  }[]
}

export interface IUnsetSeverityTopicsPayload {
  topicId: string
  positionIds: number[]
}

export type MitigationPolicyDocumentType = eMitigationPolicyDocumentType

export type BulkEnumConflictAction = 'skip' | 'use_existing' | 'create_new'
export type BulkEnumMatchType = 'exact' | 'similar'

/** Enum sheet kinds plus Assign Positions to Groups (gateway BULK_ENUMS_ASSIGN_SHEET_KIND). */
export type BulkEnumsSheetRef = eSettingsEnumKind | 'assignPositionsToGroups'

export interface IBulkEnumRowError {
  sheet: BulkEnumsSheetRef
  rowIndex: number
  field: string
  message: string
}

export interface IBulkEnumConflict {
  sheet: eSettingsEnumKind
  rowIndex: number
  uploadedName: string
  matchType: BulkEnumMatchType
  existingId: number
  existingName: string
  similarity: number
}

export interface IBulkEnumSheetSummary {
  sheet: BulkEnumsSheetRef
  totalRows: number
  validRows: number
  errorRows: number
  conflictRows: number
}

export interface IBulkEnumSuccessRow {
  sheet: BulkEnumsSheetRef
  rowIndex: number
  name: string
  positionsPreview?: string
  matchType: BulkEnumMatchType | null
  existingId?: number
  existingName?: string
}

export type BulkEnumSkippedReason = 'already_exists' | 'system_name_unrecognized' | 'system_name_only'

export interface IBulkEnumSkippedRow {
  sheet: eSettingsEnumKind
  rowIndex: number
  reason: BulkEnumSkippedReason
  message: string
  customName?: string
  systemName?: string
}

export interface IBulkEnumsValidateResult {
  totalSuccess: number
  /** Valid assignment columns. Kept separate because row 1 contains group headers, not enum rows. */
  totalAssignments?: number
  totalErrors: number
  totalConflicts: number
  requiresGroupPermission: boolean
  sheets: IBulkEnumSheetSummary[]
  errors: IBulkEnumRowError[]
  conflicts: IBulkEnumConflict[]
  successRows?: IBulkEnumSuccessRow[]
  skippedRows?: IBulkEnumSkippedRow[]
  totalSkipped?: number
}

export interface IBulkEnumDecision {
  sheet: eSettingsEnumKind
  rowIndex: number
  action: BulkEnumConflictAction
  existingId?: number
}

export interface IBulkEnumsImportRowResult {
  sheet: BulkEnumsSheetRef
  rowIndex: number
  name: string
  action: BulkEnumConflictAction | 'created' | 'assigned'
  success: boolean
  id?: number
  message?: string
}

export interface IBulkEnumsImportResult {
  created: number
  skipped: number
  mapped: number
  failed: number
  results: IBulkEnumsImportRowResult[]
}

export const settingsApi = createApi({
  reducerPath: 'settingsApi',
  tagTypes: ['Settings', 'SsoSettings'],
  baseQuery: createBaseQueryWithErrorHandling(`${configManager.api.GATEWAY_URL}/settings`, (headers, { getState }) => {
    headers.set('x-request-id', uuidv4())
    const { token, companyToken } = (getState() as RootState).authReducer
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    if (companyToken) {
      headers.set('x-company-token', companyToken)
    }
    return headers
  }),
  endpoints: (builder) => ({
    getSettings: builder.query<IClientSettings, string>({
      query: (locale) => ({
        url: `?locale=${locale}`,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IClientSettings>, meta) => {
        return rawResult.data as IClientSettings
      },
      providesTags: [{ type: 'Settings', id: 'LIST' }],
    }),
    updateGroupPositions: builder.mutation<IClientSettingsPositionGroup[], IClientSettingsPositionGroup[]>({
      query: (body) => ({
        url: '/enum/groups/positions',
        method: 'PUT',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IClientSettingsPositionGroup[]>, meta) => {
        return rawResult.data as IClientSettingsPositionGroup[]
      },
    }),
    updateSeverityPositionSettings: builder.mutation<IClientSettings, ISeverityPositionItem[]>({
      query: (body) => ({
        url: '/severity',
        method: 'PUT',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IClientSettings>, meta) => {
        return rawResult.data as IClientSettings
      },
      invalidatesTags: [{ type: 'Settings', id: 'LIST' }],
    }),
    resetSeverityPositionSettings: builder.mutation<IClientSettings, number[]>({
      query: (body) => ({
        url: '/severity',
        method: 'DELETE',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IClientSettings>, meta) => {
        return rawResult.data as IClientSettings
      },
      invalidatesTags: [{ type: 'Settings', id: 'LIST' }],
    }),
    unsetSeverityTopicsForPositions: builder.mutation<IClientSettings, IUnsetSeverityTopicsPayload>({
      query: (body) => ({
        url: '/severity/topics',
        method: 'PATCH',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IClientSettings>, meta) => {
        return rawResult.data as IClientSettings
      },
      invalidatesTags: [{ type: 'Settings', id: 'LIST' }],
    }),
    updateCompanyInfoSettings: builder.mutation<ICompanyInfo, Partial<ICompanyInfo>>({
      query: (body) => ({
        url: '/company-info',
        method: 'PUT',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<ICompanyInfo>, meta) => {
        return rawResult.data as ICompanyInfo
      },
      invalidatesTags: [{ type: 'Settings', id: 'LIST' }],
    }),
    updateUXSettings: builder.mutation<IClientSettingsCommonUX, Partial<IClientSettingsCommonUX>>({
      query: (body) => ({
        url: '/ux',
        method: 'PUT',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IClientSettingsCommonUX>, meta) => {
        return rawResult.data as IClientSettingsCommonUX
      },
      invalidatesTags: [{ type: 'Settings', id: 'LIST' }],
    }),
    updateImpacts: builder.mutation<IImpactsResponseSettings, Partial<IImpactsRequestSettings>>({
      query: (body) => ({
        url: '/impact/associate',
        method: 'PUT',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IImpactsResponseSettings>, meta) => {
        return rawResult.data as IImpactsResponseSettings
      },
    }),
    updateDeleteImpacts: builder.mutation<IImpactsResponseSettings, Partial<IImpactsRequestSettings>>({
      query: (body) => ({
        url: '/impact/unassociate',
        method: 'PUT',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IImpactsResponseSettings>, meta) => {
        return rawResult.data as IImpactsResponseSettings
      },
    }),
    addEnumSettingsItem: builder.mutation<IClientSettingsEnums, { type: eSettingsEnumKind; name: string }>({
      query: (body) => ({
        url: '/enum',
        method: 'POST',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IClientSettingsEnums>, meta) => {
        return rawResult.data as IClientSettingsEnums
      },
    }),
    deleteEnumSettingsItem: builder.mutation<IClientSettingsEnums, { type: eSettingsEnumKind; id: number }>({
      query: (body) => ({
        url: '/enum',
        method: 'DELETE',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IClientSettingsEnums>, meta) => {
        return rawResult.data as IClientSettingsEnums
      },
    }),
    updateEnumSettingsItem: builder.mutation<IClientSettingsEnums, { type: eSettingsEnumKind; id: number; name: string }>({
      query: (body) => ({
        url: '/enum',
        method: 'PUT',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IClientSettingsEnums>, meta) => {
        return rawResult.data as IClientSettingsEnums
      },
    }),
    checkEnumUsage: builder.query<{ isUsed: boolean; employeeCount: number }, { enumType: eSettingsEnumKind; enumId: number }>({
      query: ({ enumType, enumId }) => ({
        url: `/enum/${enumType}/${enumId}/usage`,
      }),
      transformResponse: (rawResult: ILCHttpResponse<{ isUsed: boolean; employeeCount: number }>, meta) => {
        return rawResult.data as { isUsed: boolean; employeeCount: number }
      },
    }),
    updateEmailSettings: builder.mutation<IClientSettingsEmail, Partial<IClientSettingsEmail>>({
      query: (body) => ({
        url: '/emails',
        method: 'PUT',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IClientSettingsEmail>, meta) => {
        return rawResult.data as IClientSettingsEmail
      },
      invalidatesTags: [{ type: 'Settings', id: 'LIST' }],
    }),
    getCompanyLogoTmpUrl: builder.query<ITmpURL, void>({
      query: () => ({
        url: '/tmp-url/logo',
      }),
      transformResponse: (rawResult: ILCHttpResponse<ITmpURL>, meta) => {
        return rawResult.data as ITmpURL
      },
    }),
    uploadCompanyLogo: builder.mutation<IUploadCompanyLogoResponse, FormData>({
      query: (formData) => ({
        url: '/tmp-url/logo',
        method: 'PUT',
        body: formData,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IUploadCompanyLogoResponse>, meta) => {
        return rawResult.data as IUploadCompanyLogoResponse
      },
    }),
    updateAssessmentLinksSettings: builder.mutation<IClientSettingsRiskHR, IClientSettingsAssessmentLink[]>({
      query: (body) => ({
        url: '/assessment-links',
        method: 'PUT',
        body: { assessmentLinks: body },
      }),
      transformResponse: (rawResult: ILCHttpResponse<IClientSettingsRiskHR>, meta) => {
        return rawResult.data as IClientSettingsRiskHR
      },
      invalidatesTags: [{ type: 'Settings', id: 'LIST' }],
    }),
    assignUnassignedPositionsToOthersGroup: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: '/enum/positions/assign-unassigned',
        method: 'POST',
      }),
      transformResponse: (rawResult: ILCHttpResponse<{ message: string }>, meta) => {
        return rawResult.data as { message: string }
      },
    }),
    // Mitigation policies endpoints
    getMitigationPoliciesStatus: builder.query<IMitigationPolicyStatus[], void>({
      query: () => ({
        url: '/mitigation-policies',
      }),
      transformResponse: (rawResult: ILCHttpResponse<IMitigationPolicyStatus[]>, meta) => {
        return rawResult.data as IMitigationPolicyStatus[]
      },
    }),
    getMitigationPolicyFiles: builder.query<IMitigationPolicyFileSummary[], eMitigationPolicyDocumentType>({
      query: (type) => ({
        url: `/mitigation-policies/files?type=${type}`,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IMitigationPolicyFileSummary[]>, meta) => {
        return rawResult.data as IMitigationPolicyFileSummary[]
      },
    }),
    uploadMitigationPolicy: builder.mutation<IMitigationPolicyUploadResponse, FormData>({
      query: (formData) => ({
        url: '/mitigation-policies/upload',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IMitigationPolicyUploadResponse>, meta) => {
        return rawResult.data as IMitigationPolicyUploadResponse
      },
    }),
    assignMitigationPolicy: builder.mutation<
      IMitigationPolicyAssignResponse,
      { type: eMitigationPolicyDocumentType; fileId: string; topicIds: number[] }
    >({
      query: (body) => ({
        url: '/mitigation-policies/assign',
        method: 'POST',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IMitigationPolicyAssignResponse>, meta) => {
        return rawResult.data as IMitigationPolicyAssignResponse
      },
    }),
    getMitigationPolicyTmpUrl: builder.query<ITmpURL, { topicId: number; type: eMitigationPolicyDocumentType }>({
      query: ({ topicId, type }) => ({
        url: `/mitigation-policies/${topicId}/${type}/tmp-url`,
      }),
      transformResponse: (rawResult: ILCHttpResponse<ITmpURL>, meta) => {
        return rawResult.data as ITmpURL
      },
    }),
    getMitigationPolicyFileTmpUrl: builder.query<ITmpURL, string>({
      query: (fileId) => ({
        url: `/mitigation-policies/files/${fileId}/tmp-url`,
      }),
      transformResponse: (rawResult: ILCHttpResponse<ITmpURL>, meta) => {
        return rawResult.data as ITmpURL
      },
    }),
    deleteMitigationPolicyFromAllTopics: builder.mutation<IMitigationPolicyDeleteAllResponse, eMitigationPolicyDocumentType>({
      query: (type) => ({
        url: `/mitigation-policies/all/${type}`,
        method: 'DELETE',
      }),
      transformResponse: (rawResult: ILCHttpResponse<IMitigationPolicyDeleteAllResponse>, meta) => {
        return rawResult.data as IMitigationPolicyDeleteAllResponse
      },
    }),

    // Topic Assignments (owner / collaborator / escalation contact)
    getTopicAssignments: builder.query<ITopicAssignmentsResponse, void>({
      query: () => ({ url: '/topic-assignments' }),
      transformResponse: (rawResult: ILCHttpResponse<ITopicAssignmentsResponse>) => rawResult.data as ITopicAssignmentsResponse,
      providesTags: [{ type: 'Settings', id: 'TOPIC_ASSIGNMENTS' }],
    }),
    upsertTopicAssignments: builder.mutation<ITopicAssignmentsResponse, { assignments: ITopicAssignment[] }>({
      query: (body) => ({ url: '/topic-assignments', method: 'PUT', body }),
      transformResponse: (rawResult: ILCHttpResponse<ITopicAssignmentsResponse>) => rawResult.data as ITopicAssignmentsResponse,
      invalidatesTags: [{ type: 'Settings', id: 'TOPIC_ASSIGNMENTS' }],
    }),
    clearTopicAssignment: builder.mutation<ITopicAssignmentsResponse, number>({
      query: (topicId) => ({ url: `/topic-assignments/${topicId}`, method: 'DELETE' }),
      transformResponse: (rawResult: ILCHttpResponse<ITopicAssignmentsResponse>) => rawResult.data as ITopicAssignmentsResponse,
      invalidatesTags: [{ type: 'Settings', id: 'TOPIC_ASSIGNMENTS' }],
    }),
    resetTopicAssignmentsCategory: builder.mutation<ITopicAssignmentsResponse, { topicIds: number[] }>({
      query: (body) => ({ url: '/topic-assignments/reset-category', method: 'POST', body }),
      transformResponse: (rawResult: ILCHttpResponse<ITopicAssignmentsResponse>) => rawResult.data as ITopicAssignmentsResponse,
      invalidatesTags: [{ type: 'Settings', id: 'TOPIC_ASSIGNMENTS' }],
    }),
    updateUnassignedTopicSettings: builder.mutation<IUnassignedTopicSettings, IUnassignedTopicSettings>({
      query: (body) => ({ url: '/topic-assignments/unassigned-settings', method: 'PUT', body }),
      transformResponse: (rawResult: ILCHttpResponse<IUnassignedTopicSettings>) => rawResult.data as IUnassignedTopicSettings,
      invalidatesTags: [{ type: 'Settings', id: 'TOPIC_ASSIGNMENTS' }],
    }),

    // SSO endpoints
    getSsoSettings: builder.query<ISsoSettingsWithSpInfo, void>({
      query: () => ({
        url: '/sso',
        method: 'GET',
      }),
      transformResponse: (rawResult: ILCHttpResponse<ISsoSettingsWithSpInfo>) => {
        return rawResult.data as ISsoSettingsWithSpInfo
      },
      providesTags: [{ type: 'SsoSettings', id: 'SSO' }],
    }),
    fetchSsoMetadata: builder.mutation<ISsoMetadataPreview, { idpMetadataUrl: string }>({
      query: (body) => ({
        url: '/sso/fetch-metadata',
        method: 'POST',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<ISsoMetadataPreview>) => {
        return rawResult.data as ISsoMetadataPreview
      },
    }),
    updateSsoSettings: builder.mutation<ISsoSettings, ISsoSettings>({
      query: (body) => ({
        url: '/sso',
        method: 'PUT',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<ISsoSettings>) => {
        return rawResult.data as ISsoSettings
      },
      invalidatesTags: [{ type: 'SsoSettings', id: 'SSO' }],
    }),
    downloadBulkEnumsExcelTemplate: builder.query<Blob, string | undefined>({
      query: (locale) => ({
        url: locale ? `/enums/bulk/excel-template?locale=${encodeURIComponent(locale)}` : '/enums/bulk/excel-template',
        method: 'GET',
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    validateBulkEnumsExcel: builder.mutation<IBulkEnumsValidateResult, { formData: FormData; locale?: string }>({
      query: ({ formData, locale }) => ({
        url: locale ? `/enums/bulk/validate?locale=${encodeURIComponent(locale)}` : '/enums/bulk/validate',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IBulkEnumsValidateResult>) => {
        return rawResult.data as IBulkEnumsValidateResult
      },
    }),
    importBulkEnumsExcel: builder.mutation<IBulkEnumsImportResult, { formData: FormData; locale?: string }>({
      query: ({ formData, locale }) => ({
        url: locale ? `/enums/bulk/import?locale=${encodeURIComponent(locale)}` : '/enums/bulk/import',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IBulkEnumsImportResult>) => {
        return rawResult.data as IBulkEnumsImportResult
      },
      invalidatesTags: [{ type: 'Settings', id: 'LIST' }],
    }),
  }),
})

export const {
  useLazyGetSettingsQuery,
  useUpdateCompanyInfoSettingsMutation,
  useUpdateUXSettingsMutation,
  useResetSeverityPositionSettingsMutation,
  useUnsetSeverityTopicsForPositionsMutation,
  useUpdateSeverityPositionSettingsMutation,
  useAddEnumSettingsItemMutation,
  useDeleteEnumSettingsItemMutation,
  useUpdateEnumSettingsItemMutation,
  useUpdateEmailSettingsMutation,
  useUpdateImpactsMutation,
  useUpdateDeleteImpactsMutation,
  useUpdateGroupPositionsMutation,
  useLazyGetCompanyLogoTmpUrlQuery,
  useUploadCompanyLogoMutation,
  useCheckEnumUsageQuery,
  useLazyCheckEnumUsageQuery,
  useUpdateAssessmentLinksSettingsMutation,
  useAssignUnassignedPositionsToOthersGroupMutation,
  useGetMitigationPoliciesStatusQuery,
  useLazyGetMitigationPoliciesStatusQuery,
  useGetMitigationPolicyFilesQuery,
  useLazyGetMitigationPolicyFilesQuery,
  useUploadMitigationPolicyMutation,
  useAssignMitigationPolicyMutation,
  useLazyGetMitigationPolicyTmpUrlQuery,
  useLazyGetMitigationPolicyFileTmpUrlQuery,
  useDeleteMitigationPolicyFromAllTopicsMutation,
  useGetTopicAssignmentsQuery,
  useUpsertTopicAssignmentsMutation,
  useClearTopicAssignmentMutation,
  useResetTopicAssignmentsCategoryMutation,
  useUpdateUnassignedTopicSettingsMutation,
  useGetSsoSettingsQuery,
  useFetchSsoMetadataMutation,
  useUpdateSsoSettingsMutation,
  useLazyDownloadBulkEnumsExcelTemplateQuery,
  useValidateBulkEnumsExcelMutation,
  useImportBulkEnumsExcelMutation,
} = settingsApi
